/**
 * What a child sees, shown to their parent, and kept current while it is open.
 *
 * "Can I see Ravi's page?" needed a tablet before this screen existed. The
 * data was always here -- a parent holds the family key and has already
 * decrypted everything on Home -- but nothing assembled it into the shape the
 * child is actually looking at.
 *
 * IT IS THE PARENT'S OWN SESSION, and that is the whole point of doing it
 * this way. There was briefly a version that signed the parent's browser into
 * the child's account for real, by running the link flow with the parent on
 * both ends. It worked, and it cost more than it was worth: it made a device
 * on every tap, spent one of the child's ten slots, and left a working child
 * session in the parent's browser afterwards -- a page anybody at that
 * keyboard could then open with no credential at all, because a child's page
 * deliberately has no password. This asks for nothing, grants nothing, and
 * leaves nothing behind.
 *
 * THE MARKUP IS DELIBERATELY THE CHILD'S. Same living valley, same order, same
 * words: the balance first, then what they are saving for, then what
 * happened. A parent checking what their child can see is not helped by a
 * parent-shaped summary of it, and the closer this is to the real page the
 * more useful the answer is.
 *
 * LIVE, in the way the rest of the app is live. It opens instantly on the
 * state Home already decrypted, then watches the REVISION route -- one
 * GetItem on one small item -- and re-reads the family only when the number
 * actually moves. So a parent who adds money in another tab, or whose
 * co-parent does, sees this page change; and a parent who leaves it open
 * costs a few cents a month rather than a full state read every ten seconds.
 * That is the same bargain `Home` makes while a device is waiting, and the
 * reason `readRevision` exists at all.
 *
 * THE BUILDER: the child's page has a button top right that opens it, so
 * this one does too -- and since the person using it is a parent, the look is
 * saved straight away rather than sent for approval.
 *
 * AND THEIR MONEY (2026-10-28). A parent who has tapped a child can add to or
 * remove from that child's money here, and move money in and out of their
 * goals or put in their own, without going back to Home and picking the child
 * again. Every entry says "Added by Parent" / "Removed by Parent" on the
 * child's own page. See ChildMoney.tsx.
 */

import { BackButton } from '../components/BackButton';
import { useState } from 'react';
import { LivingValley } from '../valley/LivingValley';
import { isNight } from '../valley/scene';
import { Builder } from '../avatar/Builder';
import { BuilderLaunch } from '../avatar/BuilderLaunch';
import { RememberedJar } from '../valley/MarbleJar';
import { bigAmount, dollars } from '../family/money';
import { childView } from '../family/childView';
import { ChildGoal, kidDate, postcardArt } from '../child/ChildApp';
import { ParentNav } from './ParentNav';
import type { Tab } from './Home';
import { useLiveState } from '../family/useLiveState';
import { saveLook, type FamilyState } from '../family/state';
import { ChildMoney, ParentGoalActions } from './ChildMoney';
import { splitLine } from '../family/split';

export function ChildPage({
  familyId,
  familyKey,
  initial,
  personId,
  onBack,
  onTab,
}: {
  familyId: string;
  familyKey: CryptoKey;
  /** What Home already had, so this opens without a spinner. */
  initial: FamilyState;
  personId: string;
  onBack: () => void;
  /** The parent's own menu, kept on this page: it is a parent looking. */
  onTab?: (tab: Tab) => void;
}) {
  // Shared with the full valley, which needs exactly the same loop. See
  // `useLiveState` for why it watches the revision rather than the state.
  const { state, stale, refresh } = useLiveState(familyId, familyKey, initial);

  const view = childView(state, personId);
  const person = state.people.find((p) => p.id === personId);
  const [styling, setStyling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');

  if (!view) {
    // `childView` answers null for somebody not in the roster, which here
    // means they were removed in another tab while this was open.
    return (
      <main className="shell shell-plain">
        <section className="panel">
          <h2>That page is gone</h2>
          <p>This person is no longer in your family.</p>
          <div className="actions">
            <button className="primary" onClick={onBack}>
              Back
            </button>
          </div>
        </section>
      </main>
    );
  }

  if (styling && person) {
    return (
      <Builder
        initial={person.avatar}
        name={person.name}
        saveLabel="Save this look"
        busy={saving}
        error={problem}
        backLabel={`Back to ${person.name ? `${person.name}’s` : 'their'} page`}
        note="It changes straight away, on every screen in the family."
        onSave={(spec, name) => {
          setSaving(true);
          setProblem('');
          void saveLook(familyId, familyKey, person, spec, name)
            .then(async () => {
              setStyling(false);
              await refresh();
            })
            .catch((error: unknown) =>
              setProblem(error instanceof Error ? error.message : 'That did not save.'),
            )
            .finally(() => setSaving(false));
        }}
        onCancel={() => {
          setStyling(false);
          setProblem('');
        }}
      />
    );
  }

  // THE CHILD'S OWN LAYOUT (2026-10-13), not a narrow card: the same valley
  // filling the screen, their money over it and the sheet beside or under
  // it, as ChildApp draws it -- with a way back where "I'm done" would be,
  // and the goals as they see them but without their buttons.
  const night = isNight(new Date().getHours());
  const active = view.goals.filter((goal) => goal.status === 'active');
  const done = view.goals.filter((goal) => goal.status === 'completed');
  const setAside = active.reduce((sum, goal) => sum + goal.mine, 0);
  const free = view.balance - setAside;
  const plan = state.allowance?.[personId] ?? null;
  const art = postcardArt(view.balance, night);
  const locked = (id: string) => Boolean(state.goals.find((goal) => goal.id === id)?.locked);
  const whose = view.name ? `${view.name}’s` : 'Their';

  return (
    <main className="shell child child-own child-preview">
      {/* The parent's menu stays: this is a parent looking at a child's page,
          not the child's device (2026-10-13). Along the top on a wide screen,
          the usual bar along the bottom of a phone. */}
      {onTab ? <ParentNav tab="family" onTab={onTab} /> : null}
      <div className="kid">
        <section className="kid-scene" aria-label={`${whose} valley`}>
          <div className="child-valley kid-valley">
            <LivingValley
              balance={view.balance}
              night={night}
              people={view.avatar ? [{ id: personId, name: view.name, avatar: view.avatar }] : []}
            />
            {person ? (
              <BuilderLaunch
                spec={view.avatar}
                label={`Change ${view.name ? `${view.name}’s` : 'their'} character`}
                onOpen={() => setStyling(true)}
              />
            ) : null}
          </div>

          <div className="kid-top">
            <BackButton label="Back to the family" className="kid-back" onClick={onBack} />
            <p className="kid-preview-tag">{whose} page &middot; what they see</p>
            <p className="visually-hidden">This is what they see when they open Birbly. It updates as the family does.</p>
          </div>

          <div className="kid-hero">
            <p className="kid-hero-tag">{whose} page &middot; what they see</p>
            <h1 className="kid-name">{view.name || 'Their page'}</h1>
            <div className="kid-figure-row">
              <RememberedJar id={`preview-${personId}`} hundredths={view.balance} size={84} />
              <p className="total-figure kid-balance">
                {bigAmount(view.balance).number}
                {bigAmount(view.balance).word ? <span className="total-unit"> {bigAmount(view.balance).word}</span> : null}
              </p>
            </div>
            <p className="kid-line">
              <span className="visually-hidden">{view.name ? `${view.name}, this is yours. ` : 'This is yours. '}</span>
              {dollars(Math.max(0, free))} free
              {setAside > 0 ? <> &middot; {dollars(setAside)} in goals</> : null}
              {plan && !plan.paused ? (
                <>
                  {' '}&middot; +{dollars(plan.amount)} on{' '}
                  {new Date(`${plan.next}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' })}
                </>
              ) : null}
            </p>
          </div>
        </section>

        <div className="kid-sheet">
          <span className="sheet-handle" aria-hidden="true" />
          {stale ? (
            <p className="footnote">
              Something changed and this could not be refreshed.{' '}
              <button className="text-button" onClick={() => void refresh().catch(() => {})}>
                Try again
              </button>
            </p>
          ) : null}

          {person ? (
            <ChildMoney
              familyId={familyId}
              familyKey={familyKey}
              person={person}
              free={free}
              onSaved={refresh}
            />
          ) : null}

          {active.length || done.length ? (
            <section className="kid-goals-block">
              <div className="section-head">
                <h2>{view.name ? `${view.name}’s goals` : 'Their goals'}</h2>
                {done.length ? <span className="kid-done-count">{done.length} done</span> : null}
              </div>
              <div className="kid-goals">
                {done.map((goal) => (
                  <div className="goal goal-done postcard" key={goal.id}>
                    <span className="postcard-art" style={{ backgroundImage: art }} aria-hidden="true" />
                    <div className="postcard-body">
                      <p className="postcard-eyebrow">They did it</p>
                      <p className="goal-title">{goal.title || 'A goal'}</p>
                      <p className="goal-money">Saved {dollars(goal.target)}</p>
                    </div>
                  </div>
                ))}
                {active.map((goal) => {
                  const full = state.goals.find((one) => one.id === goal.id);
                  return (
                    <div className="parent-goal" key={goal.id}>
                      <ChildGoal goal={{ ...goal, locked: locked(goal.id) }} free={free} art={art} />
                      {person && full ? (
                        <ParentGoalActions
                          familyId={familyId}
                          familyKey={familyKey}
                          person={person}
                          goal={full}
                          free={free}
                          state={state}
                          onSaved={refresh}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <p className="footnote">
                The bar is everything in the goal, the same for every child on it.
                Their page shows their own part but never who put in the rest.
              </p>
            </section>
          ) : null}

          <section className="kid-history">
            <h2>What happened</h2>
            {view.entries.length === 0 ? (
              <p className="footnote">Nothing yet.</p>
            ) : (
              <ul className="entries">
                {view.entries.slice(0, 20).map((entry) => (
                  <li className={entry.voided ? 'entry voided' : 'entry'} key={entry.id}>
                    <div className="entry-body">
                      <p className="entry-reason">{entry.reason || 'A grown-up added this'}</p>
                      {(() => {
                        const line = splitLine(
                          [{ name: view.name, share: entry.amount, saved: entry.saved }],
                          (id) => state.goals.find((goal) => goal.id === id)?.title ?? '',
                        );
                        return line ? <p className="entry-split">{line}</p> : null;
                      })()}
                      <p className="entry-date">{kidDate(entry.date, state.today)}</p>
                    </div>
                    <p className={entry.amount < 0 ? 'entry-amount out' : 'entry-amount in'}>
                      {entry.amount > 0 ? '+' : ''}
                      {dollars(entry.amount)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className="footnote kid-private">
            Only {view.name || 'they'} can see this page. It does not show anybody else&rsquo;s money.
          </p>
          <footer className="legal-links kid-legal">
            <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a>
          </footer>
        </div>
      </div>
    </main>
  );
}
